import axios from "axios";
import { API } from "./apiClient";
import {
  TRAIL_PROPOSAL_DIFFICULTIES,
  TRAIL_PROPOSAL_OTHER,
  TRAIL_PROPOSAL_TYPES,
  TRAIL_PROPOSAL_RIDING_STYLES,
  TRAIL_PROPOSAL_SURFACES,
} from "./trailProposal";

const http = axios.create({ baseURL: API, withCredentials: true });

export const SUGGEST_EDIT_OTHER = TRAIL_PROPOSAL_OTHER;

const STATUS_OPTIONS = [
  { value: "OPEN", label: "Open", help: "Currently open for riding." },
  { value: "CLOSED", label: "Closed", help: "Currently closed for riding." },
  { value: "UNKNOWN", label: "Not sure / unknown", help: "Use this when the current status cannot be verified." },
];

const DIFFICULTY_OPTIONS = [
  ...TRAIL_PROPOSAL_DIFFICULTIES,
  { value: SUGGEST_EDIT_OTHER, label: "Other / suggest new…" },
];

export const SUGGEST_EDIT_FIELDS = {
  DESTINATION: [
    { value: "name", label: "Name", input: "text", placeholder: "Correct destination name" },
    { value: "operationalStatus", label: "Operational status", input: "select", options: STATUS_OPTIONS, help: "Current open/closed status of the destination." },
    { value: "summary", label: "Summary", input: "textarea", placeholder: "Short summary shown in destination cards and overview." },
    { value: "description", label: "Description", input: "textarea", placeholder: "Describe the destination, riding area and what visitors should know." },
    { value: "website", label: "Website", input: "url", placeholder: "https://official-website.example" },
    { value: "openingHours", label: "Opening hours", input: "textarea", placeholder: "e.g. Sat–Sun 10:00–17:00; check official status before travelling." },
    { value: "season", label: "Season", input: "text", placeholder: "e.g. April–October, weather dependent" },
    { value: "parking", label: "Parking", input: "textarea", placeholder: "Where riders should park and any important access notes." },
  ],
  TRAIL: [
    { value: "name", label: "Name", input: "text", placeholder: "Correct trail name" },
    { value: "operationalStatus", label: "Operational status", input: "select", options: STATUS_OPTIONS, help: "Current open/closed status of this trail." },
    { value: "difficulty", label: "Difficulty", input: "select-other", options: DIFFICULTY_OPTIONS, placeholder: "Suggest a new difficulty label" },
    { value: "lengthKm", label: "Length (m)", input: "number", min: 0, step: 1, placeholder: "e.g. 850", unitTransform: "metersToKm" },
    { value: "elevationGainM", label: "Elevation gain (m)", input: "number", min: 0, step: 1, placeholder: "e.g. 25" },
    { value: "elevationLossM", label: "Elevation loss (m)", input: "number", min: 0, step: 1, placeholder: "e.g. 180" },
    { value: "trailType", label: "Trail type", input: "select-other", options: TRAIL_PROPOSAL_TYPES, placeholder: "Suggest a new trail type" },
    { value: "ridingStyle", label: "Riding style", input: "select-other", options: TRAIL_PROPOSAL_RIDING_STYLES, placeholder: "Suggest a new riding style" },
    { value: "surface", label: "Surface", input: "select-other", options: TRAIL_PROPOSAL_SURFACES, placeholder: "Suggest a new surface" },
    { value: "description", label: "Description", input: "textarea", placeholder: "Describe the trail character, features and anything riders should know." },
  ],
};
export const suggestEditFieldsForTarget = (targetType) => SUGGEST_EDIT_FIELDS[targetType] || [];

export const suggestEditFieldConfig = (targetType, field) =>
  suggestEditFieldsForTarget(targetType).find((entry) => entry.value === field) || null;

export const nextSuggestEditField = (targetType, changes = []) => {
  const used = new Set(changes.map((change) => change.field).filter(Boolean));
  return suggestEditFieldsForTarget(targetType).find((field) => !used.has(field.value))?.value || "";
};

export const emptySuggestEditForm = (targetType) => ({
  changes: [{ field: nextSuggestEditField(targetType), proposedValue: "", otherValue: "" }],
  reason: "",
  sourceUrl: "",
  sourceNote: "",
  contactName: "",
  contactEmail: "",
});

export const normalizeSuggestEditInput = ({ targetType, targetId, form }) => ({
  target_type: targetType,
  target_id: targetId,
  changes: (form.changes || [])
    .map((change) => {
      const field = String(change.field || "").trim();
      const config = suggestEditFieldConfig(targetType, field);
      let value = String(change.proposedValue ?? "").trim();
      if (value === SUGGEST_EDIT_OTHER) value = String(change.otherValue ?? "").trim();
      if (config?.unitTransform === "metersToKm" && value) {
        const meters = Number(value);
        value = Number.isFinite(meters) ? String(meters / 1000) : value;
      }
      return { field, proposed_value: value };
    })
    .filter((change) => change.field && change.proposed_value),
  reason: String(form.reason || "").trim(),
  source_url: String(form.sourceUrl || "").trim() || null,
  source_note: String(form.sourceNote || "").trim() || null,
  contact_name: String(form.contactName || "").trim() || null,
  contact_email: String(form.contactEmail || "").trim() || null,
});

export const validateSuggestEditForm = (input) => {
  if (!input.changes.length) return "Add at least one field and proposed value.";
  if (!input.reason) return "Tell us why this should be corrected.";
  return null;
};

export async function submitSuggestEdit(input) {
  const response = await http.post("", {
    query: `mutation SubmitSuggestEdit($input: SuggestEditInput!) {
      submitSuggestEdit(input: $input) { id target_type target_id status submitted_at }
    }`,
    variables: { input },
  });
  if (response.data.errors?.length) throw new Error(response.data.errors[0].message);
  return response.data.data.submitSuggestEdit;
}
